// Bridges the player's per-frame visual state to the MP4 recorder. Owns the
// offscreen output canvas, a cached blurred backdrop, the caption font, and the
// recorder. The player builds a CaptureSourceState each rAF tick and calls tick();
// this composites it (compositor.drawFrame) and pushes the frame to the encoder.
// Created only when capturing AND WebCodecs is supported (else create() → null).
import { drawFrame, coverImageRect, type FrameDims, type CaptureFrame } from "./compositor";
import { Mp4Recorder, type CaptureRecorder } from "./recorder";
import { MediaRecorderSink } from "./mediaRecorderSink";
import type { CaptureTier } from "./support";
import type { CaptionFont } from "./captionCanvas";
import type { ZoomResult, VisibleWord, Mode } from "../types";
import type { CaptionMask, BleepMode } from "../../config/censorship";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE } from "../../config/censorship";

export interface CaptureSourceState {
  /** main slide/photo: the live <img> element + a stable cache key (its src). */
  bg: { el: CanvasImageSource; src: string } | null;
  /** zoom computed at EXPORT dims (null = none). */
  zoom: ZoomResult | null;
  /** deck slide-out overlay (outgoing image + progress 0→1). */
  slideOut: { img: CanvasImageSource; progress: number } | null;
  /** doodle reveal + fade opacity. */
  doodle: { img: CanvasImageSource; opacity: number } | null;
  /** the live Rive character canvas. */
  rive: CanvasImageSource | null;
  captions: { words: VisibleWord[]; mode: Mode; highlightActive: boolean };
}

export interface CaptureControllerDeps {
  recorder?: CaptureRecorder;
  canvas?: HTMLCanvasElement;
  doc?: Document;
}

export interface CaptureControllerArgs {
  dims: FrameDims;
  captionFont: CaptionFont;
  /** Caption redaction style; default "vowels" (== today). */
  captionMask?: CaptionMask;
  /** Roast's bleep mode; default "tone". Gates soundalike stand-in redaction in captions. */
  bleepMode?: BleepMode;
  /** Sink tier from detectCaptureTier(); default "webcodecs" (Tier-A, unchanged). */
  tier?: CaptureTier;
  /** Recordable MP4 mime from pickMp4Mime() — required in practice for tier "mediarecorder". */
  mimeType?: string;
  audioTrack?: MediaStreamTrack | null;
  /** Real AudioContext sample rate (so the muxer/encoder match the captured audio). */
  audioSampleRate?: number;
  /** Real captured channel count. */
  audioChannels?: number;
  /** export-dims ÷ on-screen-stage-height, so the backdrop blur matches the live look. */
  backdropScale?: number;
  fps?: number;
  deps?: CaptureControllerDeps;
}

function natOf(src: CanvasImageSource): { w: number; h: number } {
  const a = src as unknown as { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
  return { w: a.naturalWidth || a.width || 0, h: a.naturalHeight || a.height || 0 };
}

/** Build the capture sink for a tier. Tier-A is the existing Mp4Recorder (untouched);
 *  Tier-B records the controller's own canvas via captureStream + MediaRecorder. */
export function createRecorderForTier(tier: CaptureTier, opts: {
  canvas: HTMLCanvasElement;
  dims: FrameDims;
  fps?: number;
  audioTrack?: MediaStreamTrack | null;
  mimeType?: string;
  audioSampleRate?: number;
  audioChannels?: number;
}): CaptureRecorder {
  if (tier === "mediarecorder") {
    return new MediaRecorderSink({
      canvas: opts.canvas,
      dims: opts.dims,
      fps: opts.fps,
      audioTrack: opts.audioTrack ?? null,
      mimeType: opts.mimeType ?? "video/mp4",
    });
  }
  return new Mp4Recorder({
    dims: opts.dims,
    fps: opts.fps,
    audioTrack: opts.audioTrack ?? null,
    audioSampleRate: opts.audioSampleRate,
    audioChannels: opts.audioChannels,
  });
}

/** iOS WebKit has no Canvas-2D `filter` — and because the missing IDL attribute
 *  makes a plain write-then-read probe pass (expando semantics), the only honest
 *  probe is a rendered one: blur a 1px dot and check the ink actually smeared. */
export function canvasFilterSupported(doc: Document = document): boolean {
  try {
    const c = doc.createElement("canvas");
    c.width = 9;
    c.height = 9;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    ctx.filter = "blur(4px)";
    ctx.fillStyle = "#fff";
    ctx.fillRect(4, 4, 1, 1);
    ctx.filter = "none";
    // A real blur smears ink 2px off-center; without filter support that pixel is 0.
    return ctx.getImageData(2, 4, 1, 1).data[3] > 0;
  } catch {
    return false;
  }
}

export class CaptureController {
  private constructor(
    private dims: FrameDims,
    private captionFont: CaptionFont,
    private recorder: CaptureRecorder,
    private canvas: HTMLCanvasElement,
    private ctx: CanvasRenderingContext2D | null,
    private doc: Document,
    private backdropBlurPx: number,
    private captionMask: CaptionMask,
    private bleepMode: BleepMode,
    private filterSupported: boolean,
  ) {}

  static async create(args: CaptureControllerArgs): Promise<CaptureController | null> {
    const doc = args.deps?.doc ?? document;
    const canvas = args.deps?.canvas ?? doc.createElement("canvas");
    canvas.width = args.dims.w;
    canvas.height = args.dims.h;
    const recorder: CaptureRecorder = args.deps?.recorder ?? createRecorderForTier(args.tier ?? "webcodecs", {
      canvas,
      dims: args.dims,
      fps: args.fps,
      audioTrack: args.audioTrack ?? null,
      mimeType: args.mimeType,
      audioSampleRate: args.audioSampleRate,
      audioChannels: args.audioChannels,
    });
    const started = await recorder.start();
    if (!started) return null;
    const ctx = canvas.getContext("2d");
    // On-screen backdrop is a fixed blur(24px); scale it to export by stage→export ratio.
    // Fall back to the ~960px-stage assumption (dims.h/960) when no scale is supplied.
    const scale = args.backdropScale && args.backdropScale > 0 ? args.backdropScale : args.dims.h / 960;
    const backdropBlurPx = Math.max(1, Math.round(24 * scale));
    const filterSupported = canvasFilterSupported(doc);
    return new CaptureController(args.dims, args.captionFont, recorder, canvas, ctx, doc, backdropBlurPx, args.captionMask ?? DEFAULT_CAPTION_MASK, args.bleepMode ?? DEFAULT_BLEEP_MODE, filterSupported);
  }

  private backdropCanvas: HTMLCanvasElement | null = null;
  private backdropCtx: CanvasRenderingContext2D | null = null;
  private backdropSrc = "";
  private backdrop: CanvasImageSource | null = null;

  /** Render (and cache) the blurred+darkened backdrop when the bg image changes. */
  private ensureBackdrop(bg: CaptureSourceState["bg"]): void {
    if (!bg) { this.backdrop = null; this.backdropSrc = ""; return; }
    if (bg.src === this.backdropSrc && this.backdrop) return;
    if (!this.backdropCanvas) {
      this.backdropCanvas = this.doc.createElement("canvas");
      this.backdropCanvas.width = this.dims.w;
      this.backdropCanvas.height = this.dims.h;
      this.backdropCtx = this.backdropCanvas.getContext("2d");
    }
    const bctx = this.backdropCtx;
    if (!bctx) { this.backdrop = null; return; }
    const n = natOf(bg.el);
    if (!n.w || !n.h) { this.backdrop = null; return; }
    bctx.clearRect(0, 0, this.dims.w, this.dims.h);
    const r = coverImageRect(this.dims, n.w, n.h, 1.1);
    if (this.filterSupported) {
      bctx.filter = `blur(${this.backdropBlurPx}px) brightness(0.4)`;
      bctx.drawImage(bg.el, r.x, r.y, r.w, r.h);
      bctx.filter = "none";
    } else {
      // Fallback blur (iOS WebKit: no Canvas-2D filter): a single downscale/upscale
      // reads too sharp — bilinear downsampling PRESERVES local energy, so a bright
      // shape survives as a bright blob where a real Gaussian would dim it by
      // spreading. Instead: successive halvings (each is a 2×2 box filter; stacked
      // box filters converge on a Gaussian), then two 3×3 box passes at the small
      // size via nine offset composites at running-average alpha (spreads/dims
      // concentrated energy with zero pixel loops), then a stepped upscale (giant
      // single upscales show bilinear diamonds). Verified side-by-side against the
      // native ctx.filter render. All drawImage — runs once per background change.
      const k = Math.max(2, Math.round(this.backdropBlurPx / 2));
      const targetW = Math.max(2, Math.ceil(this.dims.w / (k * 4)));
      const mk = (w: number, h: number): { el: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null => {
        const c = this.doc.createElement("canvas");
        c.width = w;
        c.height = h;
        const cctx = c.getContext("2d");
        return cctx ? { el: c, ctx: cctx } : null;
      };

      let cw = Math.max(1, Math.ceil(this.dims.w / 2));
      let ch = Math.max(1, Math.ceil(this.dims.h / 2));
      const seed = mk(cw, ch);
      // No scratch ctx → give up on the backdrop for this bg; tick keeps running.
      if (!seed) { this.backdrop = null; return; }
      seed.ctx.imageSmoothingEnabled = true;
      seed.ctx.drawImage(bg.el, r.x / 2, r.y / 2, r.w / 2, r.h / 2);
      let cur: HTMLCanvasElement = seed.el;

      // Successive halvings down to ~targetW — each halving is a 2×2 box filter, and
      // stacked box filters converge toward a Gaussian.
      while (cw / 2 >= targetW) {
        const nw = Math.max(1, Math.ceil(cw / 2));
        const nh = Math.max(1, Math.ceil(ch / 2));
        const next = mk(nw, nh);
        if (!next) break; // keep what we have — never throw, never blank the backdrop
        next.ctx.imageSmoothingEnabled = true;
        next.ctx.drawImage(cur, 0, 0, cw, ch, 0, 0, nw, nh);
        cur = next.el; cw = nw; ch = nh;
      }

      // Two 3×3 box-blur passes at the small size, each via nine offset composites —
      // spreads/dims concentrated energy with zero per-pixel loops. TRAP: a constant
      // 1/9 alpha does NOT average — sequential source-over at fixed 1/9 converges to
      // only ~0.65 opacity after nine taps (1-(8/9)^9, ~0.49 after both passes → the
      // black base shows through ~20%) and weights late taps heaviest. A running
      // average instead: tap i (1-based) composites at 1/i, so the first tap is fully
      // opaque and each later tap folds in equally — a true uniform box average.
      for (let pass = 0; pass < 2; pass++) {
        const box = mk(cw, ch);
        if (!box) break;
        box.ctx.imageSmoothingEnabled = true;
        let tap = 0;
        for (let dy = -1; dy <= 1; dy++)
          for (let dx = -1; dx <= 1; dx++) {
            box.ctx.globalAlpha = 1 / (++tap);
            box.ctx.drawImage(cur, dx, dy);
          }
        cur = box.el;
      }

      // Stepped (three-hop) upscale back to export dims — a single giant upscale shows
      // bilinear diamond artifacts. Each hop runs only when it genuinely upscales:
      // for small k the blur chain stops above dims/16 (or even dims/4), so early hops
      // would DOWNscale and re-sharpen nothing — skip them and just do fewer hops.
      const up = (w: number, h: number, from: HTMLCanvasElement): HTMLCanvasElement => {
        if (w <= from.width || h <= from.height) return from; // not an upscale → skip hop
        const c = mk(w, h);
        if (!c) return from; // keep what we have — never throw, never blank the backdrop
        c.ctx.imageSmoothingEnabled = true;
        c.ctx.drawImage(from, 0, 0, from.width, from.height, 0, 0, w, h);
        return c.el;
      };
      const mid1 = up(Math.max(1, Math.ceil(this.dims.w / 16)), Math.max(1, Math.ceil(this.dims.h / 16)), cur);
      const mid2 = up(Math.max(1, Math.ceil(this.dims.w / 4)), Math.max(1, Math.ceil(this.dims.h / 4)), mid1);

      bctx.imageSmoothingEnabled = true;
      (bctx as CanvasRenderingContext2D & { imageSmoothingQuality?: string }).imageSmoothingQuality = "high";
      bctx.drawImage(mid2, 0, 0, this.dims.w, this.dims.h);
      bctx.fillStyle = "rgba(0, 0, 0, 0.6)";
      bctx.fillRect(0, 0, this.dims.w, this.dims.h);
    }
    this.backdrop = this.backdropCanvas;
    this.backdropSrc = bg.src;
  }

  /** Compose + encode one frame. `timeSec` is the audio master clock (audioEl.currentTime). */
  tick(state: CaptureSourceState, timeSec: number): void {
    if (!this.ctx) return; // no 2d context (e.g. test/headless) → nothing to compose
    this.ensureBackdrop(state.bg);
    const frame: CaptureFrame = {
      dims: this.dims,
      bg: state.bg?.el ?? null,
      backdrop: this.backdrop,
      zoom: state.zoom,
      slideOut: state.slideOut,
      doodle: state.doodle,
      rive: state.rive,
      captions: state.captions,
      captionFont: this.captionFont,
      captionMask: this.captionMask,
      bleepMode: this.bleepMode,
    };
    drawFrame(this.ctx, frame);
    this.recorder.pushVideoFrame(this.canvas, Math.max(0, timeSec) * 1e6);
  }

  finish(): Promise<Blob | null> {
    return this.recorder.finish();
  }

  abort(): void {
    this.recorder.abort();
  }
}
